/* @flow */
// typecheck: ignore file
import _ from 'lodash';
import cx from 'classnames';
import React from 'react';

import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph.jsx';
import Menu from 'components/Menu.jsx';
import MenuItem from 'components/MenuItem.jsx';
import Organization from 'entities/Organization';
import Text from 'components/Text.jsx';
import Image from 'components/Image.jsx';
import MediaObject from 'components/MediaObject.jsx';
import { mountModal } from 'lib/modal';
import { patchRoles, patchCurrentUserRoles } from 'admin/actions/OrgRoleActions';
import type { PotentialRole } from 'groups/components/OrgRoleDropdown.jsx';

import './styles/OrgRoleDropdownMenu.scss';

import AppleImage from 'assets/images/apple.svg';
import HeartImage from 'assets/images/heart.svg';
import PencilImage from 'assets/images/pencil.svg';
import BellImage from 'assets/images/bell.svg';

type Props = {
  organization: Organization,
  roles: string[],
  potentialRoles: PotentialRole[],
  userId: number,
  isCurrentUser?: boolean,
  onClose?: (roles: string[]) => void,
  onSave?: () => void,
  onRequestHide?: () => void,
};

class OrgRoleDropdownMenu extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.roles = this.getRolesObject(props.roles);
  }

  componentWillReceiveProps(nextProps: Props): void {
    if (!_.isEqual(this.props.roles, nextProps.roles)) {
      this.roles = this.getRolesObject(nextProps.roles);
    }
  }

  roles: Object;

  getRolesObject(roles: string[]) {
    return {
      student: roles.includes('student'),
      parent: roles.includes('parent'),
      teacher: roles.includes('teacher'),
      admin: roles.includes('admin'),
      rce: roles.includes('rce'),
    };
  }

  getRoles() {
    return _.keys(this.roles).filter(r => this.roles[r]);
  }

  getFormattedRoles(): string {
    return this.getRoles().join(', ');
  }

  handleClose = () => {
    const { organization, userId, roles, onClose } = this.props;
    if (_.xor(roles, this.getRoles()).length) {
      const commitPatchRole = () => {
        const patchFn = this.props.isCurrentUser
          ? patchCurrentUserRoles
          : patchRoles;

        patchFn(organization.id, userId, this.getRoles()).then(this.props.onSave);

        if (onClose) {
          onClose(this.getRoles());
        }
      };

      const revertPatchRole = () => {
        this.roles = this.getRolesObject(this.props.roles);
      };

      if (CurrentUser.getUserId() === userId) {
        mountModal(
          <ConfirmDialog
            eventScreenName="OrgRoleDropdownMenu"
            title={'Are you sure you want to change your role?'}
            actionButtonText="Yes"
            cancelButtonText="No"
            onConfirm={commitPatchRole}
            onCancel={revertPatchRole}
          />,
          this,
        );
      } else {
        commitPatchRole();
      }
    }
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  }

  renderMenuItem(role: string, roleName: string, icon: string) {
    const checked = this.roles[role];
    const disabled = role === 'admin' && this.roles.admin;

    const handleClick = () => {
      const nextRoles = { ...this.roles };
      nextRoles[role] = !this.roles[role];
      if (role === 'student' && nextRoles.student) {
        nextRoles.parent = false;
        nextRoles.teacher = false;
      } else if (nextRoles.teacher || nextRoles.parent || nextRoles.admin) {
        nextRoles.student = false;
      }

      if (role === 'admin' && !this.roles.parent) {
        nextRoles.parent = true;
      }

      this.roles = nextRoles;
    };

    return (
      <MenuItem
        key={role}
        className={cx('OrgRoleDropdownMenu-item', { 'OrgRoleDropdownMenu-item-hover': !disabled })}
        onClick={disabled ? () => {} : handleClick}>
        <div className="OrgRoleDropdownMenu-item-inner">
          <MediaObject gutter="small" center>
            <Image
              className="OrgRoleDropdownMenu-item-image"
              src={icon}
            />
            <Text className="OrgRoleDropdownMenu-item-label" color={checked ? 'pitch' : 'inherit'}>
              {roleName}
            </Text>
          </MediaObject>
          {checked && <Glyph name="check" size="medium" color="brand" />}
          {disabled && <div className="OrgRoleDropdownMenu-item-disabled" />}
        </div>
      </MenuItem>
    );
  }

  render() {
    const roleToIcon = {
      student: PencilImage,
      parent: HeartImage,
      teacher: AppleImage,
      admin: BellImage,
    };

    return (
      <Menu
        {...this.props}
        className="OrgRoleDropdownMenu"
        onRequestHide={this.handleClose}>
        {this.props.potentialRoles.map(({ role, display_role }) => {
          return this.renderMenuItem(role, display_role, roleToIcon[role]);
        })}
      </Menu>
    );
  }
}

export default OrgRoleDropdownMenu;



// WEBPACK FOOTER //
// ./src/groups/components/OrgRoleDropdownMenu.jsx